import { AuthError } from './errors.ts'

/** PKCE (RFC 7636) with Web Crypto. ~40 lines, so no dependency: the flow is the thing
 * docs/design/frontend-v1.md §5 wants encapsulated here, and a library would only hide it. */

export interface PkcePair {
  readonly verifier: string
  readonly challenge: string
}

/** base64url per RFC 7636 §A: base64 minus padding, with the URL-safe alphabet. */
function base64UrlEncode(bytes: Uint8Array): string {
  let ascii = ''
  for (const byte of bytes) ascii += String.fromCharCode(byte)
  return btoa(ascii).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function subtle(): SubtleCrypto {
  // Absent on http:// origins other than localhost, where crypto.subtle is undefined.
  // Failing here beats sending a plain `code_challenge_method=S256` lie to Cognito.
  const available = globalThis.crypto?.subtle
  if (!available) {
    throw new AuthError(
      'crypto_unavailable',
      'Web Crypto is unavailable: PKCE requires a secure context (https or localhost)',
    )
  }
  return available
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length)
  globalThis.crypto.getRandomValues(bytes)
  return bytes
}

/** 32 random bytes → 43 base64url chars, inside RFC 7636's 43–128 range. */
export function createVerifier(): string {
  return base64UrlEncode(randomBytes(32))
}

/** An opaque, single-use CSRF value, checked on the way back. */
export function createState(): string {
  return base64UrlEncode(randomBytes(16))
}

export async function challengeFor(verifier: string): Promise<string> {
  const digest = await subtle().digest('SHA-256', new TextEncoder().encode(verifier))
  return base64UrlEncode(new Uint8Array(digest))
}

export async function createPkcePair(): Promise<PkcePair> {
  const verifier = createVerifier()
  return { verifier, challenge: await challengeFor(verifier) }
}
